




















import { Component, Vue, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { WordSearchPuzzle, Cell } from '@/wordsearch/types';
import PuzzleWordMark from '@/components/PuzzleWordMark.vue';
import { getSelection } from '@/wordsearch/utils';
import { playSound } from '@/utils/playSound';

const puzzle = namespace('Puzzle');

@Component({
  components: {
    PuzzleWordMark,
  },
})
export default class PuzzleGrid extends Vue {
  @puzzle.State
  public puzzle!: WordSearchPuzzle;

  @puzzle.State
  private gridSize!: number;

  @puzzle.State
  private userAnswers!: Record<string, string[]>;

  @puzzle.State
  private hintCell!: string | null;

  @puzzle.Mutation
  private setSelection!: (ids: string[]) => void;

  @puzzle.Getter
  private cellWidth!: number;

  @puzzle.Getter
  private paused!: boolean;

  @puzzle.Getter
  private solved!: boolean;

  @puzzle.Getter
  private cellHeight!: number;

  @puzzle.Action
  private submitAnswer!: (cells: Cell[]) => void;

  @puzzle.Mutation
  private setHintCell!: (cell: string | null) => void;

  @puzzle.Action
  public tickTimer!: () => void;

  @Prop({ default: false }) private printVersion!: boolean;

  private tickPuzzleIntervalId: number | null = null;

  private clickedCell: Cell | null = null;

  private overCell: Cell | null = null;

  get hidden() {
    return !this.solved && this.paused;
  }

  get selectedCells(): Cell[] {
    if (this.clickedCell === null || this.overCell === null) return [];

    const selection = getSelection(this.puzzle, this.clickedCell, this.overCell);
    this.setSelection(selection.map((cell) => cell.id));
    return selection;
  }

  get selectedWord(): string {
    return this.selectedCells.map((cell) => cell.letter).join('');
  }

  get mappedAnswers() {
    return Object.values(this.userAnswers)
      .map((ids) => ([
        this.puzzle.cells.find((cell) => cell.id === ids[0]),
        this.puzzle.cells.find((cell) => cell.id === ids[ids.length - 1]),
      ]));
  }

  get partOfAnswerCells() {
    return Object.values(this.userAnswers).flat();
  }

  get gridStyle() {
    return `
      width: ${this.gridSize}px;
      height: ${this.gridSize}px;
      min-width: ${this.gridSize}px;
    `;
  }

  public created() {
    if (this.printVersion) return;

    window.addEventListener('pointerup', this.handlePointerUp);
    this.tickPuzzleIntervalId = setInterval(this.tickTimer, 1000);
  }

  public destoyed() {
    if (this.printVersion) return;

    window.removeEventListener('pointerup', this.handlePointerUp);
    if (this.tickPuzzleIntervalId) clearInterval(this.tickPuzzleIntervalId);
  }

  private clickOnCell(cell: Cell): void {
    playSound('letterSelectSound');
    this.clickedCell = cell;
    this.overCell = cell;
  }

  private handlePointerUp(): void {
    if (this.clickedCell === null) return;
    this.submitAnswer(this.selectedCells);
    this.clickedCell = null;
    this.overCell = null;
    this.setSelection([]);
  }

  private handlePointerMove(e: PointerEvent): void {
    if (this.clickedCell === null) return;

    const cellElement = document.elementFromPoint(
      e.pageX - window.pageXOffset, e.pageY - window.pageYOffset,
    ) as HTMLElement | null;

    if (!cellElement) return;

    const cell = this.puzzle.cells.find((c) => c.id === cellElement.dataset.cellId);

    if (cell && this.overCell !== cell) {
      this.overCell = cell;
      playSound('letterSelectSound');
      this.setHintCell(null);
    }
  }

  private getStyleForCell(cell: Cell): string {
    return `
      font-weight: ${this.partOfAnswerCells.includes(cell.id) ? 700 : 500};
      font-size: ${this.cellHeight * 0.6}px;
      left: ${this.cellWidth * cell.x}px;
      top: ${this.cellHeight * cell.y}px;
      width: ${this.cellWidth}px;
      height: ${this.cellHeight}px;
      line-height: ${this.cellHeight}px;
    `;
  }
}
